import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Définition des segments
const SEGMENTS = {
  VIP: {
    label: 'VIP',
    color: 'rgb(245, 158, 11)', // Orange
    description: 'Clients fidèles à forte valeur'
  },
  REGULAR: {
    label: 'Réguliers',
    color: 'rgb(59, 130, 246)', // Bleu
    description: 'Clients avec achats réguliers'
  },
  OCCASIONAL: {
    label: 'Occasionnels',
    color: 'rgb(16, 185, 129)', // Vert
    description: 'Clients avec achats sporadiques'
  },
  NEW: {
    label: 'Nouveaux',
    color: 'rgb(139, 92, 246)', // Violet
    description: 'Première commande'
  },
  AT_RISK: {
    label: 'À risque',
    color: 'rgb(239, 68, 68)', // Rouge
    description: 'Clients inactifs récemment'
  }
};

const CustomerSegmentation = ({ orders }) => {
  const segmentationData = useMemo(() => {
    // Grouper les commandes par client
    const customerOrders = orders.reduce((acc, order) => {
      const phone = order.customerPhone;
      if (!phone) return acc;

      if (!acc[phone]) {
        acc[phone] = [];
      }
      acc[phone].push(order);
      return acc;
    }, {});

    // Analyser chaque client
    const now = new Date();
    const customerStats = Object.entries(customerOrders).map(([phone, orders]) => {
      // Trier les commandes par date
      const sortedOrders = orders.sort((a, b) => {
        const dateA = a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt);
        const dateB = b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt);
        return dateB - dateA;
      });

      const firstOrder = sortedOrders[sortedOrders.length - 1];
      const lastOrder = sortedOrders[0];
      const firstOrderDate = firstOrder.createdAt?.seconds 
        ? new Date(firstOrder.createdAt.seconds * 1000) 
        : new Date(firstOrder.createdAt);
      const lastOrderDate = lastOrder.createdAt?.seconds 
        ? new Date(lastOrder.createdAt.seconds * 1000) 
        : new Date(lastOrder.createdAt);

      // Calculer les métriques clés
      const totalSpent = orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
      const orderCount = orders.length;
      const averageOrderValue = totalSpent / orderCount;
      const daysSinceLastOrder = Math.floor((now - lastOrderDate) / (1000 * 60 * 60 * 24));
      const daysBetweenFirstAndLast = Math.floor((lastOrderDate - firstOrderDate) / (1000 * 60 * 60 * 24)) || 1;
      const orderFrequency = orderCount > 1 ? daysBetweenFirstAndLast / (orderCount - 1) : 0;
      const confirmationRate = orders.filter(o => o.status === 'confirmed').length / orderCount;

      // Déterminer le segment
      let segment;
      if (orderCount === 1 && daysSinceLastOrder <= 30) {
        segment = 'NEW';
      } else if (daysSinceLastOrder > 90) {
        segment = 'AT_RISK';
      } else if (
        orderCount >= 5 && 
        averageOrderValue > 10000 && 
        confirmationRate > 0.8
      ) {
        segment = 'VIP';
      } else if (orderFrequency > 0 && orderFrequency <= 30) {
        segment = 'REGULAR';
      } else {
        segment = 'OCCASIONAL';
      }

      return {
        phone,
        segment,
        totalSpent,
        orderCount,
        averageOrderValue,
        daysSinceLastOrder,
        orderFrequency,
        confirmationRate,
        lastOrderDate
      };
    });

    // Calculer les statistiques par segment
    const segmentStats = Object.keys(SEGMENTS).reduce((acc, segment) => {
      const customers = customerStats.filter(c => c.segment === segment);
      const totalCustomers = customers.length;
      const totalRevenue = customers.reduce((sum, c) => sum + c.totalSpent, 0);
      const averageOrderValue = totalCustomers ? totalRevenue / totalCustomers : 0;
      const confirmationRate = customers.reduce((sum, c) => sum + c.confirmationRate, 0) / totalCustomers || 0;

      acc[segment] = {
        customerCount: totalCustomers,
        totalRevenue,
        averageOrderValue,
        confirmationRate
      };
      return acc;
    }, {});

    // Calculer l'évolution des segments dans le temps
    const last6Months = [...Array(6)].map((_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date;
    }).reverse();

    const segmentTrends = last6Months.map(date => {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      return Object.keys(SEGMENTS).reduce((acc, segment) => {
        acc[segment] = customerStats.filter(customer => {
          const orderDate = customer.lastOrderDate;
          return orderDate >= monthStart && orderDate <= monthEnd;
        }).length;
        return acc;
      }, {
        month: date.toLocaleDateString('fr-DZ', { month: 'short', year: 'numeric' })
      });
    });

    return {
      customerStats,
      segmentStats,
      segmentTrends
    };
  }, [orders]);

  // Données pour le graphique circulaire des segments
  const segmentChartData = {
    labels: Object.values(SEGMENTS).map(s => s.label),
    datasets: [
      {
        data: Object.keys(SEGMENTS).map(
          segment => segmentationData.segmentStats[segment].customerCount
        ),
        backgroundColor: Object.values(SEGMENTS).map(s => s.color)
      }
    ]
  };

  // Données pour le graphique des tendances
  const trendChartData = {
    labels: segmentationData.segmentTrends.map(t => t.month),
    datasets: Object.keys(SEGMENTS).map(segment => ({
      label: SEGMENTS[segment].label,
      data: segmentationData.segmentTrends.map(t => t[segment]),
      borderColor: SEGMENTS[segment].color,
      backgroundColor: `${SEGMENTS[segment].color}33`,
      fill: true,
      tension: 0.4
    }))
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'right'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const segment = Object.keys(SEGMENTS)[context.dataIndex];
            const stats = segmentationData.segmentStats[segment];
            const percentage = ((stats.customerCount / segmentationData.customerStats.length) * 100).toFixed(1);
            return [
              `${context.label}: ${stats.customerCount} (${percentage}%)`,
              `CA total: ${stats.totalRevenue.toLocaleString('fr-DZ')} DA`,
              `Panier moyen: ${Math.round(stats.averageOrderValue).toLocaleString('fr-DZ')} DA`,
              `Taux de conf.: ${(stats.confirmationRate * 100).toFixed(1)}%`
            ];
          }
        }
      }
    }
  };

  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">Segmentation client</h3>

        {/* Segments */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {Object.entries(SEGMENTS).map(([key, segment]) => {
            const stats = segmentationData.segmentStats[key];
            return (
              <div 
                key={key}
                className="rounded-lg p-4"
                style={{ backgroundColor: `${segment.color}10` }}
              >
                <div className="flex items-center mb-2">
                  <div 
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: segment.color }}
                  ></div>
                  <h4 className="text-sm font-medium" style={{ color: segment.color }}>
                    {segment.label}
                  </h4>
                </div>
                <p className="text-2xl font-semibold text-gray-900">
                  {stats.customerCount}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {segment.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Graphiques */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Répartition actuelle</h4>
            <div className="relative" style={{ height: '300px' }}>
              <Pie data={segmentChartData} options={pieOptions} />
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-4">Évolution sur 6 mois</h4>
            <div className="relative" style={{ height: '300px' }}>
              <Line data={trendChartData} options={lineOptions} />
            </div>
          </div>
        </div>

        {/* Recommandations */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Actions recommandées</h4>
          <ul className="space-y-2 text-sm text-gray-600">
            {segmentationData.segmentStats.AT_RISK.customerCount > 0 && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {segmentationData.segmentStats.AT_RISK.customerCount} clients à risque - Envoyer une offre de réactivation
              </li>
            )}
            {segmentationData.segmentStats.VIP.customerCount > 0 && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-orange-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Programme de fidélité pour les {segmentationData.segmentStats.VIP.customerCount} clients VIP
              </li>
            )}
            <li className="flex items-start">
              <svg className="h-5 w-5 text-blue-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Objectif : Convertir {segmentationData.segmentStats.OCCASIONAL.customerCount} clients occasionnels en réguliers
            </li>
            {segmentationData.segmentStats.NEW.customerCount > 0 && (
              <li className="flex items-start">
                <svg className="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Suivre l'expérience des {segmentationData.segmentStats.NEW.customerCount} nouveaux clients
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CustomerSegmentation;
